import React, { useEffect, useMemo, useState } from "react";

function chemistRowId(c) {
  return c?.ChemistID ?? c?.chemistID;
}

function filterChemistRows(rows, q) {
  const s = q.trim().toLowerCase();
  if (!s) return rows;
  return rows.filter((r) => {
    const name = String(r.ChemistName ?? r.chemistName ?? "").toLowerCase();
    const email = String(r.Email ?? r.email ?? "").toLowerCase();
    return name.includes(s) || email.includes(s);
  });
}

const toggleInSet = (set, id, on) => {
  const next = new Set(set);
  if (on) next.add(id);
  else next.delete(id);
  return next;
};

const AddGroupManagerChemistsModal = ({
  isOpen,
  onClose,
  managerLabel,
  chemists,
  saving,
  onConfirm,
}) => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setSelected(new Set());
    }
  }, [isOpen]);

  const filtered = useMemo(
    () => filterChemistRows(chemists || [], search),
    [chemists, search]
  );

  const selectAllVisible = () => {
    const ids = filtered
      .map((r) => chemistRowId(r))
      .map(Number)
      .filter((n) => Number.isFinite(n));
    setSelected(new Set(ids));
  };

  if (!isOpen) return null;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget && !saving) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selected.size === 0) return;
    await onConfirm(Array.from(selected).filter(Number.isFinite));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={handleBackdrop}
    >
      <div
        className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-labelledby="add-chemists-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-3 shrink-0">
          <div>
            <h2
              id="add-chemists-title"
              className="text-lg font-semibold text-gray-900"
            >
              Add chemists
            </h2>
            {managerLabel && (
              <p className="text-sm text-gray-600 mt-0.5">{managerLabel}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-500 hover:text-gray-800 p-1 rounded-lg hover:bg-gray-100 disabled:opacity-40"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 min-h-0 p-4 gap-3"
        >
          <p className="text-xs text-gray-500">
            Chemists already linked to this manager are not shown. Select rows
            and confirm to add them to this grouping.
          </p>
          <input
            type="search"
            placeholder="Filter by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={selectAllVisible}
              className="px-3 py-2 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              Select all visible
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="px-3 py-2 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              Clear selection
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-y-auto flex-1 min-h-[200px] max-h-[50vh]">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 sticky top-0 text-xs text-gray-700">
                <tr>
                  <th className="w-10 px-2 py-2" />
                  <th className="px-2 py-2 text-left font-medium">Chemist</th>
                  <th className="px-2 py-2 text-left font-medium">Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-3 py-8 text-center text-gray-500"
                    >
                      No chemists available to add (or none match this filter).
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => {
                    const cid = chemistRowId(r);
                    const idn = Number(cid);
                    const valid = Number.isFinite(idn);
                    const checked = valid && selected.has(idn);
                    return (
                      <tr key={valid ? idn : String(cid)}>
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!valid}
                            onChange={(e) =>
                              valid &&
                              setSelected(
                                toggleInSet(selected, idn, e.target.checked)
                              )
                            }
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-2 py-1.5 text-gray-900">
                          {r.ChemistName ?? r.chemistName ?? "—"}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">
                          {r.Email ?? r.email ?? "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || selected.size === 0}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Adding…" : `Add selected (${selected.size})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGroupManagerChemistsModal;
